import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserWebsite } from '../types/UserWebsite';
import { getWebsiteLookupDataFromUrl } from '../utils/api/website.utils';
import { Theme } from '../types/Theme';
import useAuthTokenIframeListener from '../hooks/useAuthTokenIframeListener';
import useIframeMessageHandler from '../hooks/useIframeMessageHandler';
import { generateFormScript } from '../utils/formScript';

const PreviewPage: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [html, setHtml] = useState('');
  const [searchParams] = useSearchParams();
  const { websiteId, websiteState, websiteType } = getWebsiteLookupDataFromUrl(searchParams);

  useAuthTokenIframeListener();

  const scrollActionMappings = useMemo(
    () => ({
      SCROLL_TO_PERCENT: (additionalData: { scrollPercent?: number }) => {
        const percent = additionalData?.scrollPercent ?? 1;
        const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
        const target = maxScroll * percent;
        window.scrollTo({ top: target, behavior: 'smooth' });
      },
      SCROLL_TO_TOP: () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
    }),
    []
  );
  useIframeMessageHandler(scrollActionMappings);

  useEffect(() => {
    if (websiteId !== null) {
      if (websiteType === 'theme') {
        Theme.fromId(websiteId).then(website => {
          setHtml(website.getHtml(websiteState));
        });
      } else {
        UserWebsite.fromId(websiteId).then(website => {
          setHtml(website.getHtml(websiteState));
        });
      }
    }
    // eslint-disable-next-line
  }, [websiteId, websiteState]);

  useEffect(() => {
    if (!html || !containerRef.current) return;

    const root = containerRef.current;

    // 1) Add websiteId to the form so we can submit it
    root.querySelectorAll('form').forEach(form => {
      if (websiteId != null) form.setAttribute('data-website-id', String(websiteId));
    });

    // 2) Inject form script into the page
    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.text = generateFormScript(); // IMPORTANT: string WITHOUT <script> tags
    containerRef.current.appendChild(s);
    return () => {
      s.remove();
    };
  }, [html, websiteId]);

  if (!websiteId || !websiteState || !websiteType) {
    return <div>No website id, state or type</div>;
  }

  return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: html }} />;
};

export default PreviewPage;
